import React from 'react';
import { Eye, Edit2, Trash2, Copy, Lock } from 'lucide-react';

/**
 * Template Card Component
 * Displays a single agreement template with responsive layout
 */
const TemplateCard = ({ 
  template, 
  isCustom = false,
  canEdit = false,
  onPreview,
  onEdit,
  onCopy,
  onDelete 
}) => {
  return (
    <div
      className={`bg-white border rounded-xl p-4 transition-shadow ${
        isCustom 
          ? 'border-purple-200 hover:shadow-md' 
          : template.isAccessible 
            ? 'border-gray-200 hover:shadow-md' 
            : 'border-gray-200 opacity-60'
      }`}
    >
      {/* Header - Title and Badge */}
      <div className="mb-3">
        <div className="flex items-start justify-between gap-2 mb-1">
          <h3 className="font-semibold text-gray-900 text-sm sm:text-base line-clamp-2 flex-1 min-w-0">
            {template.name}
          </h3>
          <div className="flex-shrink-0">
            {isCustom ? (
              <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-medium rounded-full whitespace-nowrap">
                Custom
              </span>
            ) : template.requiredTier !== 'Solo' && (
              <span className={`px-2 py-0.5 text-xs font-medium rounded-full whitespace-nowrap ${
                template.isAccessible 
                  ? 'bg-green-100 text-green-700' 
                  : 'bg-gray-100 text-gray-600'
              }`}>
                {template.requiredTier}
              </span>
            )}
          </div>
        </div>
        <p className="text-sm text-gray-500 line-clamp-2">
          {template.description || (isCustom ? 'Custom template' : 'Standard agreement template')}
        </p>
      </div>

      {/* Policy Tags - Wrap on mobile */}
      <div className="flex flex-wrap gap-1.5 mb-4">
        {template.includeCancellationPolicy && (
          <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
            Cancellation
          </span>
        )}
        {template.includePhotoConsent && (
          <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
            Photo
          </span>
        )}
        {template.includeWeatherPolicy && (
          <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
            Weather
          </span>
        )}
        {!template.includeCancellationPolicy && !template.includePhotoConsent && !template.includeWeatherPolicy && (
          <span className="px-2 py-0.5 bg-gray-50 text-gray-400 text-xs rounded">
            Basic
          </span>
        )}
      </div>

      {/* Action Buttons - Stack on very small screens */}
      <div className="flex flex-col xs:flex-row gap-2">
        {/* Preview Button - Always visible */}
        <button
          onClick={() => onPreview(template.id)}
          className="flex-1 px-3 py-2 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors flex items-center justify-center gap-1.5"
        >
          <Eye className="h-4 w-4" />
          Preview
        </button>

        {isCustom ? (
          // Custom template actions: Edit & Delete
          <>
            <button
              onClick={() => onEdit(template)}
              className="flex-1 px-3 py-2 text-sm text-blue-700 bg-blue-100 hover:bg-blue-200 rounded-lg transition-colors flex items-center justify-center gap-1.5"
            >
              <Edit2 className="h-4 w-4" />
              Edit
            </button>
            <button
              onClick={() => onDelete(template.id, template.name)}
              className="px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors flex items-center justify-center"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </>
        ) : (
          // System template actions: Copy/Customize or Locked
          <>
            {template.isAccessible && canEdit ? (
              <button
                onClick={() => onCopy(template.id)}
                className="flex-1 px-3 py-2 text-sm text-purple-700 bg-purple-100 hover:bg-purple-200 rounded-lg transition-colors flex items-center justify-center gap-1.5"
              >
                <Copy className="h-4 w-4" />
                Customize
              </button>
            ) : !template.isAccessible && (
              <span className="flex-1 px-3 py-2 text-sm text-gray-400 bg-gray-50 rounded-lg text-center flex items-center justify-center gap-1.5">
                <Lock className="h-4 w-4" />
                Upgrade
              </span>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default TemplateCard;